import { useState, useEffect, useCallback, useRef } from 'react';
import { getWebSocket } from '@/lib/websocket';
import { addBreadcrumb } from '@/lib/telemetry';

interface TypingUser {
  userId: string;
  displayName: string;
  timestamp: number;
}

interface UseChatTypingReturn {
  typingUsers: TypingUser[];
  sendTyping: () => void;
}

const TYPING_TIMEOUT_MS = 4000;

export function useChatTyping(channelId: string | null): UseChatTypingReturn {
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const lastTypingSentRef = useRef<number>(0);
  const cleanupTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const sendTyping = useCallback(() => {
    if (!channelId) return;
    const now = Date.now();
    // Rate limit: max once per 3 seconds
    if (now - lastTypingSentRef.current < 3000) return;
    lastTypingSentRef.current = now;

    try {
      const ws = getWebSocket();
      ws.sendChatTyping(channelId);
    } catch {
      addBreadcrumb('chat', 'WS not ready for sendTyping');
    }
  }, [channelId]);

  useEffect(() => {
    if (!channelId) {
      setTypingUsers([]);
      return;
    }

    let unsubscribe: (() => void) | undefined;

    try {
      const ws = getWebSocket();
      unsubscribe = ws.onChatTyping((eventChannelId, userId, displayName) => {
        if (eventChannelId !== channelId) return;
        setTypingUsers((prev) => {
          const now = Date.now();
          const filtered = prev.filter((u) => u.userId !== userId);
          return [...filtered, { userId, displayName, timestamp: now }];
        });
      });
    } catch {
      addBreadcrumb('chat', 'WS not ready for typing listener');
    }

    // Clean up stale typing indicators
    cleanupTimerRef.current = setInterval(() => {
      const cutoff = Date.now() - TYPING_TIMEOUT_MS;
      setTypingUsers((prev) => prev.filter((u) => u.timestamp > cutoff));
    }, 1000);

    return () => {
      unsubscribe?.();
      if (cleanupTimerRef.current) {
        clearInterval(cleanupTimerRef.current);
        cleanupTimerRef.current = null;
      }
      setTypingUsers([]);
    };
  }, [channelId]);

  return { typingUsers, sendTyping };
}
